import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedVersion, setSelectedVersion, availableVersions } = useConfig();

  const isActive = (path) => location.pathname === path;

  const handleVersionChange = (event) => {
    setSelectedVersion(event.target.value);
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      <a
        href="https://louca.certifia.ai"
        className="fixed top-4 right-4 z-50 hidden md:flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-semibold shadow-lg hover:shadow-xl transition"
        title="Ouvrir Louca.ai"
      >
        <span role="img" aria-label="Louca.ai">🚗</span>
        Louca.ai
      </a>
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-b border-orange-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center justify-between gap-6">
              <Link to="/inspections" className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-300 to-pink-400">
                Louca.ai
              </Link>
              <a
                href="https://louca.certifia.ai"
                className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full border border-orange-400/60 text-orange-200 hover:bg-orange-500/20 transition"
                title="Ouvrir Louca.ai"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5"
                >
                  <path d="M5.25 5.25A2.25 2.25 0 0 1 7.5 3h3.75a.75.75 0 0 1 0 1.5H7.5a.75.75 0 0 0-.75.75v9.75c0 .414.336.75.75.75h9.75a.75.75 0 0 0 .75-.75v-3.75a.75.75 0 0 1 1.5 0v3.75A2.25 2.25 0 0 1 17.25 18h-9.75A2.25 2.25 0 0 1 5.25 15.75V5.25Z" />
                  <path d="M12 6a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 .75.75v5.5a.75.75 0 0 1-1.5 0V7.81l-5.72 5.72a.75.75 0 0 1-1.06-1.06L16.19 6.75H12.75A.75.75 0 0 1 12 6Z" />
                </svg>
              </a>
              <div className="hidden sm:flex items-center gap-3 text-slate-400 text-sm">
                <span className="text-xs uppercase tracking-wider text-slate-500">Connecté</span>
                <span className="px-2 py-1 bg-slate-800/80 border border-orange-500/20 rounded-lg text-orange-100 font-medium">
                  {user?.username || 'Demo'}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
              <div className="bg-slate-900 border border-orange-500/30 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:gap-3 shadow-inner shadow-orange-500/10">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Version d'analyse</p>
                  <p className="text-sm text-orange-200">
                    {availableVersions.find((v) => v.id === selectedVersion)?.label || selectedVersion}
                  </p>
                </div>
                <div className="mt-3 sm:mt-0">
                  <select
                    value={selectedVersion}
                    onChange={handleVersionChange}
                    className="w-full sm:w-auto bg-slate-800 border border-slate-600 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  >
                    {availableVersions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={logout}
                className="px-4 py-2 bg-orange-500/20 border border-orange-500/40 text-orange-200 rounded-lg hover:bg-orange-500/30 transition"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 pb-32">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 border-t border-orange-500/20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-around">
            <NavLink to="/inspections" isActive={isActive}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Inspections
            </NavLink>

            <NavLink to="/upload" isActive={isActive}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouvelle
            </NavLink>

            <NavLink to="/compare" isActive={isActive}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Comparer
            </NavLink>

            <NavLink to="/config" isActive={isActive}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3a.75.75 0 00-.75.75v1.011a7.5 7.5 0 00-1.902.784l-.716-.715a.75.75 0 00-1.06 0l-1.06 1.06a.75.75 0 000 1.061l.715.716A7.5 7.5 0 003.5 11H2.75a.75.75 0 00-.75.75v1.5c0 .414.336.75.75.75H3.5a7.5 7.5 0 00.777 2.333l-.715.716a.75.75 0 000 1.06l1.06 1.061a.75.75 0 001.06 0l.716-.715A7.5 7.5 0 0011 20.5v.75c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V20.5a7.5 7.5 0 002.333-.777l.716.715a.75.75 0 001.06 0l1.061-1.06a.75.75 0 000-1.061l-.715-.716A7.5 7.5 0 0020.5 13h.75a.75.75 0 00.75-.75v-1.5a.75.75 0 00-.75-.75H20.5a7.5 7.5 0 00-.777-2.333l.715-.716a.75.75 0 000-1.06l-1.06-1.061a.75.75 0 00-1.061 0l-.716.715A7.5 7.5 0 0014 4.761V3.75a.75.75 0 00-.75-.75h-1.5z" />
              </svg>
              Config
            </NavLink>
          </div>
        </div>
      </nav>
    </div>
  );
};

const NavLink = ({ to, isActive, children }) => {
  const Child = ({ icon, label }) => (
    <div
      className={`flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-all ${
        isActive(to)
          ? 'text-orange-300'
          : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {icon}
      {label && <span className="text-xs">{label}</span>}
    </div>
  );

  return (
    <Link to={to}>
      <Child>{children}</Child>
    </Link>
  );
};

export default Layout;



